import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import classNames from 'classnames';
import React, { memo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routeConfiguration';
import { petrol, yellow } from '../../../../styles/mui/colors';
import { ensureCurrentUser, ensureUser } from '../../../../util/data';
import usePassPlus from '../../../../util/hooks/usePassPlus';
import IconBookings from '../../../Icons/IconBookings';
import IconCalendar from '../../../Icons/IconCalendar';
import IconFavorites from '../../../Icons/IconFavorites';
import IconLogout from '../../../Icons/IconLogout';
import IconMap from '../../../Icons/IconMap';
import IconPin from '../../../Icons/IconPin';
import IconPoints from '../../../Icons/IconPoints';
import IconSettings from '../../../Icons/IconSettings';
import IconUser from '../../../Icons/IconUser';
import NamedLink from '../../../Links/NamedLink/NamedLink';

const ProfileMenuComponent = ({
  currentUser = null,
  currentUserHasListings,
  currentPage = null,
  notificationCount,
  onLogout,
}) => {
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);
  const profileMenuOpen = Boolean(profileMenuAnchorEl);

  // Use the hook to check for Annual Pass Plus
  const [hasPlusPass] = usePassPlus();

  const handleProfileMenuClick = (event) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchorEl(null);
  };

  const userIsCurrentUser = currentUser && currentUser.type === 'currentUser';
  const avatarUser = userIsCurrentUser ? ensureCurrentUser(currentUser) : ensureUser(currentUser);
  const hasProfileImage = avatarUser.profileImage && avatarUser.profileImage.id;
  const profileImageUrl = hasProfileImage
    ? avatarUser.profileImage.attributes?.variants['square-small']?.url
    : null;

  const currentPageClass = (page) => {
    return currentPage === page || null;
  };

  const currentPageColor = (page) => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? yellow.main : petrol.main;
  };

  const avatarStyle = hasPlusPass ? { border: `3px solid ${petrol.main}` } : null;

  return (
    <Badge badgeContent={notificationCount} color="secondary">
      <Box>
        <IconButton
          aria-controls="profile-menu"
          aria-haspopup="true"
          color="inherit"
          edge="end"
          onClick={handleProfileMenuClick}
          sx={{ mr: 0, ml: 1, p: 0 }}
        >
          <Badge
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            badgeContent={hasPlusPass ? 'Plus' : null}
            color="primary"
            overlap="circular"
          >
            <Avatar src={profileImageUrl} sx={{ ...avatarStyle }} />
          </Badge>
        </IconButton>

        <Menu
          anchorEl={profileMenuAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          id="profile-menu"
          onClose={handleProfileMenuClose}
          open={profileMenuOpen}
          transformOrigin={{
            vertical: -16,
            horizontal: 'right',
          }}
        >
          <NamedLink
            className={classNames(currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <IconUser color={currentPageColor('ProfileSettingsPage')} />
              </ListItemIcon>

              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </MenuItem>
          </NamedLink>
          <NamedLink
            className={classNames(currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
          >
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <Badge badgeContent={notificationCount} color="secondary">
                  <IconBookings color={currentPageColor('InboxPage')} />
                </Badge>
              </ListItemIcon>

              <FormattedMessage id="TopbarDesktop.bookingsLink" />
            </MenuItem>
          </NamedLink>
          <NamedLink className={classNames(currentPageClass('CalendarPage'))} name="CalendarPage">
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <IconCalendar color={currentPageColor('CalendarPage')} />
              </ListItemIcon>

              <FormattedMessage id="TopbarDesktop.yourCalendarLink" />
            </MenuItem>
          </NamedLink>
          <NamedLink className={classNames(currentPageClass('BookmarkPage'))} name="BookmarkPage">
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <IconFavorites color={currentPageColor('BookmarkPage')} />
              </ListItemIcon>

              <FormattedMessage id="TopbarDesktop.bookmarkLink" />
            </MenuItem>
          </NamedLink>
          <NamedLink
            className={classNames(currentPageClass('RoutePlannerPage'))}
            name="RoutePlannerPage"
          >
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <IconMap color={currentPageColor('RoutePlannerPage')} />
              </ListItemIcon>

              <FormattedMessage id="TopbarDesktop.myTripsLink" />
              <Chip
                label="Beta"
                size="small"
                sx={{
                  ml: 1,
                  height: 18,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </MenuItem>
          </NamedLink>
          <NamedLink
            className={classNames(currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <IconPin color={currentPageColor('ManageListingsPage')} />
              </ListItemIcon>

              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </MenuItem>
          </NamedLink>

          <NamedLink
            className={classNames(currentPageClass('LoyaltyProgrammePage'))}
            name="LoyaltyProgrammePage"
          >
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <IconPoints color={currentPageColor('LoyaltyProgrammePage')} />
              </ListItemIcon>

              <FormattedMessage id="TopbarDesktop.loyaltyProgrammeLink" />
            </MenuItem>
          </NamedLink>

          <NamedLink
            className={classNames(currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <IconSettings color={currentPageColor('AccountSettingsPage')} />
              </ListItemIcon>

              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </MenuItem>
          </NamedLink>
          <Divider />
          <MenuItem onClick={onLogout}>
            <ListItemIcon>
              <IconLogout />
            </ListItemIcon>
            <FormattedMessage id="TopbarDesktop.logout" />
          </MenuItem>
        </Menu>
      </Box>
    </Badge>
  );
};

ProfileMenuComponent.displayName = 'ProfileMenu';

const ProfileMenu = memo(ProfileMenuComponent);

export default ProfileMenu;
